import useHttpProvider from "@/common/hooks/use-http";
import { AuthenticationProvider } from "@/common/providers/authentication";
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Plan } from "../models/plan";
import { Subscription } from "../models/subscription";
import * as SubscriptionServices from "../services/subscriptions";

type SubscriptionContextValue = {
	isLoading: boolean;
	currentPlan: Plan | null;
	currentSubscription: Subscription | null;
	hasActiveSubscription: boolean;
	refresh: () => Promise<void>;
};

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
	const { http } = useHttpProvider();

	const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
	const [currentSubscription, setCurrentSubscription] =
		useState<Subscription | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const refresh = useCallback(async () => {
		if (!AuthenticationProvider.isAuthenticated) {
			setCurrentPlan(null);
			setCurrentSubscription(null);
			return;
		}

		setIsLoading(true);
		try {
			const { plan, subscription } =
				await SubscriptionServices.getUserActiveSubscription(http);
			setCurrentPlan(plan ?? null);
			setCurrentSubscription(subscription ?? null);
		} catch {
			setCurrentPlan(null);
			setCurrentSubscription(null);
		} finally {
			setIsLoading(false);
		}
	}, [http]);

	useEffect(() => {
		void refresh();

		const onAuthChanged = () => {
			void refresh();
		};
		window.addEventListener("reelrewards:auth-changed", onAuthChanged);
		return () => {
			window.removeEventListener("reelrewards:auth-changed", onAuthChanged);
		};
	}, [refresh]);

	const value = useMemo<SubscriptionContextValue>(
		() => ({
			isLoading,
			currentPlan,
			currentSubscription,
			hasActiveSubscription: !!currentSubscription && !!currentPlan,
			refresh,
		}),
		[isLoading, currentPlan, currentSubscription, refresh]
	);

	return (
		<SubscriptionContext.Provider value={value}>
			{children}
		</SubscriptionContext.Provider>
	);
}

export function useSubscription() {
	const ctx = useContext(SubscriptionContext);
	if (!ctx) {
		throw new Error("useSubscription must be used within SubscriptionProvider");
	}
	return ctx;
}
